import React from "react"
import PropTypes from "prop-types"
import InfiniteCalendar, { Calendar, withRange } from "react-infinite-calendar"
import "react-infinite-calendar/styles.css"
import { Dropdown, Menu, Button } from "semantic-ui-react"
import { formatDistance, endOfDay, format, startOfDay, subDays } from "date-fns"
import ruLocale from "date-fns/locale/ru"
import bind from "bind-decorator"
import { withTranslation } from "react-i18next"

const areSameDates = (first, second) => {
  return (
    first.getDate() === second.getDate() &&
    first.getMonth() === second.getMonth() &&
    first.getFullYear() === second.getFullYear()
  )
}

const today = new Date()
const minDate = new Date(
  today.getFullYear() - 1,
  today.getMonth(),
  today.getDate(),
)

const maxDate = new Date(
  today.getFullYear() + 1,
  today.getMonth(),
  today.getDate(),
)

const dayMilliseconds = 60 * 60 * 24 * 1000

const locale = {
  blank: "Выберите период",
  headerFormat: "d MMM",
  locale: ruLocale,
  todayLabel: {
    long: "сегодн1я",
    short: "⯆",
  },
  weekdays: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
  weekStartsOn: 1,
}

const theme = {
  selectionColor: "#4d4d4d",
  textColor: {
    default: "#333",
    active: "#FFF",
  },
  accentColor: "#1B1C1D",
  weekdayColor: "#1B1C1D",
  headerColor: "#1B1C1D",
  floatingNav: {
    background: "#696969",
    color: "#FFF",
    chevron: "#FFF",
  },
  todayColor: "#1B1C1D",
}

const displayOptions = {
  showHeader: false,
}

const CalendarWithRange = withRange(Calendar)

class DateRangeDonutPicker extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      opened: false,
      pickerOpened: false,
      startDate: null,
      endDate: null,
      value: "",
    }
  }

  static format(date) {
    return format(date, "d MMM yyyy", { locale: ruLocale })
  }

  @bind
  // eslint-disable-next-line no-empty-pattern
  pickDays({}, { value }) {
    const { onRangeChange } = this.props
    let today = new Date()
    if (value < 0) {
      today = subDays(today, 1)
      value = 0
    }
    const start = subDays(today, value)
    if (this.props.resultText) {
      onRangeChange(start, today, this.props.resultText)
    } else {
      onRangeChange(start, today)
    }
    this.close()
  }

  @bind
  pickRange({ eventType, start, end }) {
    if (eventType === 3) {
      this.setState({
        startDate: start,
        endDate: end,
      })
    }
  }

  @bind
  open() {
    if (!this.state.opened) {
      this.setState({
        opened: true,
      })
    }
  }

  @bind
  close() {
    const { pickerOpened, startDate, endDate } = this.state
    const { onRangeChange } = this.props

    if (this.openPicker) {
      this.openPicker = false
    } else if (pickerOpened) {
      if (startDate && endDate) {
        if (this.props.resultText) {
          onRangeChange(startDate, endDate, this.props.resultText)
        } else {
          onRangeChange(startDate, endDate)
        }
      }
      this.setState({
        opened: false,
        pickerOpened: false,
        startDate: null,
        endDate: null,
      })
    } else {
      this.setState({
        opened: false,
      })
    }
  }

  @bind
  showPicker() {
    this.openPicker = true
    this.setState({ pickerOpened: true })
  }

  getSelectedRangeTitle() {
    const { startDate, endDate } = this.props
    if (!startDate && !endDate) {
      return "Выберите период"
    }
    const today = new Date()
    if (areSameDates(startDate, endDate)) {
      if (areSameDates(new Date(today - dayMilliseconds), startDate)) {
        return "Вчера"
      }
      if (areSameDates(new Date(today), startDate)) {
        return "Сегодня"
      }
      return DateRangeDonutPicker.format(startDate)
    }

    if (areSameDates(today, endDate)) {
      if (
        formatDistance(startOfDay(startDate), endOfDay(endDate)) ===
        "about 1 month"
      ) {
        return "1 месяц"
      }
      return formatDistance(startOfDay(startDate), endOfDay(endDate), {
        locale: ruLocale,
      })
    }

    return (
      DateRangeDonutPicker.format(startDate) +
      "-" +
      DateRangeDonutPicker.format(endDate)
    )
  }

  render() {
    const { t } = this.props
    const { opened, pickerOpened } = this.state
    const title = this.getSelectedRangeTitle()
    const range =
      ((this.state.endDate || this.props.endDate) -
        (this.state.startDate || this.props.startDate)) /
      (24 * 3600 * 1000)
    // TODO: add apply button to picker
    const picker = (
      <InfiniteCalendar
        theme={theme}
        selected={null}
        height={300}
        Component={CalendarWithRange}
        locale={locale}
        onSelect={this.pickRange}
        displayOptions={displayOptions}
        min={minDate}
        max={maxDate}
        minDate={minDate}
        maxDate={maxDate}
      />
    )
    const items = [
      <Dropdown.Item
        key={1}
        onClick={this.pickDays}
        value={0}
        content={t("today")}
      />,
      <Dropdown.Item
        key={2}
        onClick={this.pickDays}
        value={-1}
        content={t("yesterday")}
      />,
      <Dropdown.Item
        key={3}
        onClick={this.pickDays}
        value={7}
        content={`8 ${t("numDays")}`}
      />,
      <Dropdown.Item
        key={4}
        onClick={this.pickDays}
        value={30}
        content={`1 ${t("month")}`}
      />,
      <Dropdown.Item
        key={5}
        onClick={this.pickDays}
        value={90}
        content={`3 ${t("numMonth")}`}
      />,
      <Dropdown.Divider key={6} />,
      <Dropdown.Item key={7} content={t("other")} onClick={this.showPicker} />,
    ]
    return (
      <Menu compact>
        <Dropdown
          onBlur={this.close}
          onClick={this.open}
          open={opened}
          text={t("select dates")}
          style={{
            width: 130,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Dropdown.Menu>
            {picker}
            <Button
              onClick={() => {
                if (
                  this.state.startDate !== null &&
                  this.state.startDate !== undefined &&
                  this.state.endDate !== null &&
                  this.state.endDate !== undefined
                ) {
                  this.props.setShardedAnalyticDateFunction(
                    this.props.id,
                    this.state.startDate,
                    this.state.endDate,
                  )
                  this.props.refreshShardedAnalytic(this.props.id)
                }
              }}
            >
              {t("select")}
            </Button>
          </Dropdown.Menu>
        </Dropdown>
      </Menu>
    )
  }
}

DateRangeDonutPicker.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onRangeChange: PropTypes.func.isRequired,
}

export default withTranslation()(DateRangeDonutPicker)
